import React, { useState } from 'react';
import { Card, Form, Input, Button, Tabs, message, Table, Tag, Modal } from 'antd';
import { UserOutlined, LockOutlined, GiftOutlined, HistoryOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api, { formatCurrency } from '../services/api';


const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const handlePasswordChange = async (values: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    setLoading(true);
    try {
      await api.post('/auth/change-password', values);
      message.success('密码修改成功');
    } catch (error: any) {
      message.error(error.response?.data?.message || '密码修改失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async (values: { code: string }) => {
    setRedeemLoading(true);
    try {
      const response = await api.post('/users/redeem', values);
      message.success(`兑换成功！获得 ¥${response.data.amount}`);
      
      // 更新用户余额
      if (user) {
        updateUser({ balance: (user.balance || 0) + response.data.amount });
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || '兑换失败');
    } finally {
      setRedeemLoading(false);
    }
  };

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const response = await api.get('/users/orders?limit=20');
      setOrders(response.data.orders);
    } catch (error) {
      message.error('加载订单历史失败');
    } finally {
      setOrdersLoading(false);
    }
  };

  React.useEffect(() => {
    loadOrders();
  }, []);

  const getOrderTypeText = (type: string) => {
    const typeMap = {
      server_purchase: '购买服务器',
      server_renewal: '服务器续费',
      balance_recharge: '余额充值'
    };
    return typeMap[type as keyof typeof typeMap] || type;
  };

  const getStatusTag = (status: string) => {
    const statusMap = {
      completed: { color: 'green', text: '已完成' },
      pending: { color: 'orange', text: '待处理' },
      failed: { color: 'red', text: '失败' },
      cancelled: { color: 'red', text: '已取消' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  const orderColumns = [
    {
      title: '订单时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => getOrderTypeText(type),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: any) => `¥${(parseFloat(amount) || 0).toFixed(2)}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => {
        const statusTag = getStatusTag(status);
        if (status === 'cancelled' && record.refund_amount) {
          return (
            <div>
              {statusTag}
              <div className="text-xs text-green-600 mt-1">
                已退还: ¥{(parseFloat(record.refund_amount) || 0).toFixed(2)}
              </div>
            </div>
          );
        }
        return statusTag;
      },
    },
    {
      title: '服务器',
      dataIndex: 'server_name',
      key: 'server_name',
      render: (name: string) => name || '-',
    },
  ];

  return (
    <div className="fade-in">
      <h2 className="text-2xl font-bold mb-6">个人中心</h2>

      <Tabs 
        defaultActiveKey="info"
        items={[
          {
            key: 'info',
            label: '基本信息',
            icon: <UserOutlined />,
            children: (
              <Card title="账户信息" className="mb-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">用户名:</span>
                    <span className="font-medium">{user?.username}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">邮箱:</span>
                    <span className="font-medium">{user?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">角色:</span>
                    <span className="font-medium">
                      {user?.role === 'admin' ? '管理员' : '普通用户'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">账户余额:</span>
                    <span className="font-medium text-green-600">
                      ¥{formatCurrency(user?.balance)}
                    </span>
                  </div>
                </div>
              </Card>
            )
          },
          {
            key: 'password',
            label: '修改密码',
            icon: <LockOutlined />,
            children: (
              <Card title="修改密码" className="max-w-md">
                <Form onFinish={handlePasswordChange} layout="vertical">
                  <Form.Item
                    name="currentPassword"
                    label="当前密码"
                    rules={[{ required: true, message: '请输入当前密码' }]}
                  >
                    <Input.Password placeholder="输入当前密码" />
                  </Form.Item>

                  <Form.Item
                    name="newPassword"
                    label="新密码"
                    rules={[
                      { required: true, message: '请输入新密码' },
                      { min: 6, message: '密码至少6位' }
                    ]}
                  >
                    <Input.Password placeholder="输入新密码" />
                  </Form.Item>

                  <Form.Item
                    name="confirmPassword"
                    label="确认新密码"
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: '请确认新密码' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('两次输入的密码不一致'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder="确认新密码" />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      修改密码
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            )
          },
          {
            key: 'redeem',
            label: '兑换码',
            icon: <GiftOutlined />,
            children: (
              <>
                <Card title="使用兑换码" className="max-w-md mb-6">
                  <Form onFinish={handleRedeem} layout="vertical">
                    <Form.Item
                      name="code"
                      label="兑换码"
                      rules={[{ required: true, message: '请输入兑换码' }]}
                    >
                      <Input placeholder="输入兑换码，格式如：XXXX-XXXX-XXXX-XXXX" />
                    </Form.Item>

                    <Form.Item>
                      <Button type="primary" htmlType="submit" loading={redeemLoading}>
                        兑换
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>

                <Card title="兑换说明">
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>兑换码通常包含余额或服务器时长</li>
                    <li>每个兑换码只能使用一次</li>
                    <li>过期的兑换码无法使用</li>
                    <li>如有问题请联系管理员</li>
                  </ul>
                </Card>
              </>
            )
          },
          {
            key: 'orders',
            label: '订单历史',
            icon: <HistoryOutlined />,
            children: (
              <Card title="订单历史">
                <Table
                  columns={orderColumns}
                  dataSource={orders}
                  rowKey="id"
                  loading={ordersLoading}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            )
          }
        ]}
      />
    </div>
  );
};

export default Profile;



